import { useEffect, useState } from 'react';
import {
  MetricSubsection,
  ThresholdSliderMetricSection,
  ThresholdPreviewChips,
  ThresholdValueUnitRow,
  avgDurationThresholdRowTemplateColumns,
  buildPreviewValues,
  frequencyThresholdRowTemplateColumns,
  isSingularValue,
  toFrequencyPreviewUnit,
} from '@/features/projects/components/metricThresholds';
import { ProjectSettingsAccordion } from '@/features/projects/components/ProjectSettingsAccordion';
import { SettingsPanelActionRow } from '@/features/projects/components/SettingsPanelActionRow';
import {
  avgDurationUnitOptions,
  frequencyUnitOptions,
  type MetricThresholdDrafts,
  parseAvgDurationSeconds,
  parseFrequencyPerDay,
  parseThresholdValue,
  type PreferredUnits,
} from '@/features/projects/metricThresholdDrafts';
import { s, type SpacingToken } from '@/theme/tokens';
import { Box, Stack } from '@/ui';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { formatFrequency } from '@/ui/formatters/formatFrequency';
import { Switch } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import type { Dispatch, ReactElement, RefObject, SetStateAction } from 'react';

type MetricThresholdsSectionProps = {
  title: string;
  description?: string;
  passRateLabel?: string;
  frequencyLabel?: string;
  frequencySubjectFormatter?: (value: string) => string;
  avgDurationContextLabel?: string;
  drafts: MetricThresholdDrafts;
  setDrafts: Dispatch<SetStateAction<MetricThresholdDrafts>>;
  preferredUnitsRef: RefObject<PreferredUnits>;
  initialEnabled: MetricThresholdEnabledState;
  isDirty: boolean;
  isValid: boolean;
  isPending: boolean;
  baselineKey?: string;
  gridSpacing: SpacingToken;
  container?: 'panel' | 'none';
  disabled?: boolean;
  disabledReason?: string;
  onCancel: () => void;
  onSubmit: (enabled: MetricThresholdEnabledState) => void;
};

export type MetricThresholdEnabledState = {
  passRate: boolean;
  frequency: boolean;
  flakeScore: boolean;
  avgDuration: boolean;
};

export const MetricThresholdsSection = ({
  title,
  description,
  passRateLabel = 'Success Rate',
  frequencyLabel = 'Run Frequency',
  frequencySubjectFormatter,
  avgDurationContextLabel = 'per run',
  drafts,
  setDrafts,
  preferredUnitsRef,
  initialEnabled,
  isDirty,
  isValid: _isValid,
  isPending,
  baselineKey,
  gridSpacing: _gridSpacing,
  container = 'panel',
  disabled = false,
  disabledReason,
  onCancel,
  onSubmit,
}: MetricThresholdsSectionProps): ReactElement => {
  const partialInitialEnabled = initialEnabled as Partial<MetricThresholdEnabledState> | undefined;
  const resolvedInitialEnabled: MetricThresholdEnabledState = {
    passRate: partialInitialEnabled?.passRate ?? true,
    frequency: partialInitialEnabled?.frequency ?? true,
    flakeScore: partialInitialEnabled?.flakeScore ?? true,
    avgDuration: partialInitialEnabled?.avgDuration ?? true,
  };
  const [enabled, setEnabled] = useState<MetricThresholdEnabledState>(resolvedInitialEnabled);
  const [hasInteracted, setHasInteracted] = useState(false);
  const partialDrafts = drafts as
    | Partial<Pick<MetricThresholdDrafts, 'passRate' | 'frequency' | 'avgDuration' | 'flakeScore'>>
    | undefined;
  const passRateDraft = partialDrafts?.passRate ?? { target: '', warn: '' };
  const flakeScoreDraft = partialDrafts?.flakeScore ?? { target: '', warn: '' };
  const frequencyDraft = partialDrafts?.frequency ?? {
    targetValue: '',
    targetUnit: 'days',
    warnValue: '',
    warnUnit: 'days',
  };
  const avgDurationDraft = partialDrafts?.avgDuration ?? {
    targetValue: '',
    targetUnit: 'seconds',
    warnValue: '',
    warnUnit: 'seconds',
  };

  useEffect(() => {
    setEnabled(resolvedInitialEnabled);
  }, [
    resolvedInitialEnabled.passRate,
    resolvedInitialEnabled.frequency,
    resolvedInitialEnabled.flakeScore,
    resolvedInitialEnabled.avgDuration,
  ]);

  useEffect(() => {
    setHasInteracted(false);
  }, [baselineKey]);

  const isEnabledDirty =
    enabled.passRate !== resolvedInitialEnabled.passRate ||
    enabled.frequency !== resolvedInitialEnabled.frequency ||
    enabled.flakeScore !== resolvedInitialEnabled.flakeScore ||
    enabled.avgDuration !== resolvedInitialEnabled.avgDuration;
  const hasPendingChanges = (isDirty || isEnabledDirty) && hasInteracted;

  const passRateValid =
    parseThresholdValue(passRateDraft.warn) !== null &&
    parseThresholdValue(passRateDraft.target) !== null;
  const frequencyValid =
    parseFrequencyPerDay(frequencyDraft.warnValue, frequencyDraft.warnUnit) !== null &&
    parseFrequencyPerDay(frequencyDraft.targetValue, frequencyDraft.targetUnit) !== null;
  const avgDurationValid =
    parseAvgDurationSeconds(avgDurationDraft.warnValue, avgDurationDraft.warnUnit) !== null &&
    parseAvgDurationSeconds(avgDurationDraft.targetValue, avgDurationDraft.targetUnit) !== null;

  const canSubmit =
    hasPendingChanges &&
    !isPending &&
    (!enabled.passRate || passRateValid) &&
    (!enabled.frequency || frequencyValid) &&
    (!enabled.flakeScore ||
      (parseThresholdValue(flakeScoreDraft.warn) !== null &&
        parseThresholdValue(flakeScoreDraft.target) !== null)) &&
    (!enabled.avgDuration || avgDurationValid);

  const defaultFrequencySubjectFormatter = (value: string): string => {
    const singular = isSingularValue(value);
    return singular ? 'run per' : 'runs per';
  };
  const resolvedFrequencySubjectFormatter =
    frequencySubjectFormatter ?? defaultFrequencySubjectFormatter;

  const parsedPassRateTarget = parseThresholdValue(passRateDraft.target);
  const parsedPassRateWarn = parseThresholdValue(passRateDraft.warn);
  const parsedFlakeScoreTarget = parseThresholdValue(flakeScoreDraft.target);
  const parsedFlakeScoreWarn = parseThresholdValue(flakeScoreDraft.warn);
  const passRateTargetValue = Math.min(100, Math.max(0, parsedPassRateTarget ?? 90));
  const passRateWarnValue = Math.min(100, Math.max(0, parsedPassRateWarn ?? 75));
  const passRateRange: [number, number] =
    passRateWarnValue <= passRateTargetValue
      ? [passRateWarnValue, passRateTargetValue]
      : [passRateTargetValue, passRateWarnValue];
  const flakeScoreTargetValue = Math.min(100, Math.max(0, parsedFlakeScoreTarget ?? 10));
  const flakeScoreWarnValue = Math.min(100, Math.max(0, parsedFlakeScoreWarn ?? 25));
  const flakeScoreRange: [number, number] =
    flakeScoreTargetValue <= flakeScoreWarnValue
      ? [flakeScoreTargetValue, flakeScoreWarnValue]
      : [flakeScoreWarnValue, flakeScoreTargetValue];
  const frequencyWarnPerDay = parseFrequencyPerDay(
    frequencyDraft.warnValue,
    frequencyDraft.warnUnit,
  );
  const frequencyTargetPerDay = parseFrequencyPerDay(
    frequencyDraft.targetValue,
    frequencyDraft.targetUnit,
  );
  const avgDurationWarnSeconds = parseAvgDurationSeconds(
    avgDurationDraft.warnValue,
    avgDurationDraft.warnUnit,
  );
  const avgDurationTargetSeconds = parseAvgDurationSeconds(
    avgDurationDraft.targetValue,
    avgDurationDraft.targetUnit,
  );

  const frequencyThresholds =
    frequencyWarnPerDay !== null && frequencyTargetPerDay !== null
      ? {
          warn: frequencyWarnPerDay,
          target: frequencyTargetPerDay,
          direction: 'higherBetter' as const,
        }
      : null;

  const previewFrequencyUnit =
    frequencyDraft.warnUnit === frequencyDraft.targetUnit
      ? toFrequencyPreviewUnit(frequencyDraft.warnUnit)
      : undefined;
  const previewFrequencyStep = (() => {
    if (previewFrequencyUnit === 'hour') return 0.1 / 24;
    if (previewFrequencyUnit === 'day') return 0.1;
    if (previewFrequencyUnit === 'week') return 0.1 / 7;
    if (previewFrequencyUnit === 'month') return 0.1 / 30;
    return 0.1;
  })();

  const avgDurationThresholds =
    avgDurationWarnSeconds !== null && avgDurationTargetSeconds !== null
      ? {
          warn: avgDurationWarnSeconds,
          target: avgDurationTargetSeconds,
          direction: 'lowerBetter' as const,
        }
      : null;

  const passRatePreviewValues = buildPreviewValues(
    { warn: passRateRange[0], target: passRateRange[1], direction: 'higherBetter' },
    { min: 0, max: 100, step: 1 },
  );
  const flakeScorePreviewValues = buildPreviewValues(
    { warn: flakeScoreRange[1], target: flakeScoreRange[0], direction: 'lowerBetter' },
    { min: 0, max: 100, step: 1 },
  );

  const frequencyPreviewValues =
    frequencyThresholds !== null
      ? buildPreviewValues(frequencyThresholds, {
          min: 0,
          max: Math.max(
            frequencyThresholds.target + previewFrequencyStep,
            frequencyThresholds.warn + previewFrequencyStep,
          ),
          step: previewFrequencyStep,
        })
      : null;

  const avgDurationPreviewValues =
    avgDurationThresholds !== null
      ? buildPreviewValues(avgDurationThresholds, {
          min: 0,
          max: Math.max(avgDurationThresholds.warn + 1, avgDurationThresholds.target + 1),
          step: 1,
        })
      : null;
  const frequencyTargetHasError =
    parseFrequencyPerDay(frequencyDraft.targetValue, frequencyDraft.targetUnit) === null;
  const frequencyWarnHasError =
    parseFrequencyPerDay(frequencyDraft.warnValue, frequencyDraft.warnUnit) === null;
  const avgDurationTargetHasError =
    parseAvgDurationSeconds(avgDurationDraft.targetValue, avgDurationDraft.targetUnit) === null;
  const avgDurationWarnHasError =
    parseAvgDurationSeconds(avgDurationDraft.warnValue, avgDurationDraft.warnUnit) === null;

  const actions = (
    <SettingsPanelActionRow>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disabled={!hasPendingChanges}
        onClick={() => {
          setHasInteracted(false);
          setEnabled(resolvedInitialEnabled);
          onCancel();
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        fullWidthOnMobile={false}
        disabled={!canSubmit}
        onClick={() => {
          onSubmit(enabled);
        }}
      >
        Update
      </Button>
    </SettingsPanelActionRow>
  );

  const sections = (
    <Stack gap="xl">
      <ThresholdSliderMetricSection
        title={passRateLabel}
        enabled={enabled.passRate}
        warn={passRateRange[0]}
        target={passRateRange[1]}
        formatter={(value) => `${Math.round(value)}%`}
        previewValues={passRatePreviewValues}
        onToggle={(checked) => {
          setHasInteracted(true);
          setEnabled((prev) => ({ ...prev, passRate: checked }));
        }}
        onChange={(nextWarn, nextTarget) => {
          setHasInteracted(true);
          setDrafts((prev) => ({
            ...prev,
            passRate: {
              ...prev.passRate,
              warn: String(nextWarn),
              target: String(nextTarget),
            },
          }));
        }}
      />

      <Box sx={{ borderTop: 1, borderColor: 'divider', mt: s('sm'), pt: s('xl') }}>
        <MetricSubsection
          title={frequencyLabel}
          headerAccessory={
            <Switch
              size="small"
              density="compact"
              checked={enabled.frequency}
              onChange={(_event, checked) => {
                setHasInteracted(true);
                setEnabled((prev) => ({ ...prev, frequency: checked }));
              }}
            />
          }
        >
          <Stack gap="lg">
            <Stack gap="md">
              <ThresholdValueUnitRow
                label="Warn below"
                value={frequencyDraft.targetValue}
                unit={frequencyDraft.targetUnit}
                templateColumns={frequencyThresholdRowTemplateColumns}
                options={frequencyUnitOptions}
                betweenText={resolvedFrequencySubjectFormatter(frequencyDraft.targetValue)}
                disabled={!enabled.frequency}
                useNumberField
                error={frequencyTargetHasError}
                onValueChange={(nextValue) => {
                  setHasInteracted(true);
                  setDrafts((prev) => ({
                    ...prev,
                    frequency: { ...prev.frequency, targetValue: nextValue },
                  }));
                }}
                onUnitChange={(nextValue) => {
                  setHasInteracted(true);
                  setDrafts((prev) => ({
                    ...prev,
                    frequency: { ...prev.frequency, targetUnit: nextValue },
                  }));
                  preferredUnitsRef.current = {
                    ...preferredUnitsRef.current,
                    frequencyTarget: nextValue,
                  };
                }}
              />
              <ThresholdValueUnitRow
                label="Alert below"
                value={frequencyDraft.warnValue}
                unit={frequencyDraft.warnUnit}
                templateColumns={frequencyThresholdRowTemplateColumns}
                options={frequencyUnitOptions}
                betweenText={resolvedFrequencySubjectFormatter(frequencyDraft.warnValue)}
                disabled={!enabled.frequency}
                useNumberField
                error={frequencyWarnHasError}
                onValueChange={(nextValue) => {
                  setHasInteracted(true);
                  setDrafts((prev) => ({
                    ...prev,
                    frequency: { ...prev.frequency, warnValue: nextValue },
                  }));
                }}
                onUnitChange={(nextValue) => {
                  setHasInteracted(true);
                  setDrafts((prev) => ({
                    ...prev,
                    frequency: { ...prev.frequency, warnUnit: nextValue },
                  }));
                  preferredUnitsRef.current = {
                    ...preferredUnitsRef.current,
                    frequencyWarn: nextValue,
                  };
                }}
              />
            </Stack>
            {frequencyPreviewValues && frequencyThresholds ? (
              <ThresholdPreviewChips
                values={frequencyPreviewValues}
                formatter={(value) => formatFrequency(value, previewFrequencyUnit)}
                direction="higherBetter"
                warn={frequencyThresholds.warn}
                target={frequencyThresholds.target}
                disabled={!enabled.frequency}
              />
            ) : null}
          </Stack>
        </MetricSubsection>
      </Box>

      <Box sx={{ borderTop: 1, borderColor: 'divider', mt: s('sm'), pt: s('xl') }}>
        <MetricSubsection
          title="Average Duration"
          headerAccessory={
            <Switch
              size="small"
              density="compact"
              checked={enabled.avgDuration}
              onChange={(_event, checked) => {
                setHasInteracted(true);
                setEnabled((prev) => ({ ...prev, avgDuration: checked }));
              }}
            />
          }
        >
          <Stack gap="lg">
            <Stack gap="md">
              <ThresholdValueUnitRow
                label="Warn above"
                value={avgDurationDraft.targetValue}
                unit={avgDurationDraft.targetUnit}
                templateColumns={avgDurationThresholdRowTemplateColumns}
                options={avgDurationUnitOptions}
                betweenText={avgDurationContextLabel}
                contextAtEnd
                pluralizeUnitLabelsByValue
                disabled={!enabled.avgDuration}
                useNumberField
                error={avgDurationTargetHasError}
                onValueChange={(nextValue) => {
                  setHasInteracted(true);
                  setDrafts((prev) => ({
                    ...prev,
                    avgDuration: { ...prev.avgDuration, targetValue: nextValue },
                  }));
                }}
                onUnitChange={(nextValue) => {
                  setHasInteracted(true);
                  setDrafts((prev) => ({
                    ...prev,
                    avgDuration: { ...prev.avgDuration, targetUnit: nextValue },
                  }));
                  preferredUnitsRef.current = {
                    ...preferredUnitsRef.current,
                    avgDurationTarget: nextValue,
                  };
                }}
              />
              <ThresholdValueUnitRow
                label="Alert above"
                value={avgDurationDraft.warnValue}
                unit={avgDurationDraft.warnUnit}
                templateColumns={avgDurationThresholdRowTemplateColumns}
                options={avgDurationUnitOptions}
                betweenText={avgDurationContextLabel}
                contextAtEnd
                pluralizeUnitLabelsByValue
                disabled={!enabled.avgDuration}
                useNumberField
                error={avgDurationWarnHasError}
                onValueChange={(nextValue) => {
                  setHasInteracted(true);
                  setDrafts((prev) => ({
                    ...prev,
                    avgDuration: { ...prev.avgDuration, warnValue: nextValue },
                  }));
                }}
                onUnitChange={(nextValue) => {
                  setHasInteracted(true);
                  setDrafts((prev) => ({
                    ...prev,
                    avgDuration: { ...prev.avgDuration, warnUnit: nextValue },
                  }));
                  preferredUnitsRef.current = {
                    ...preferredUnitsRef.current,
                    avgDurationWarn: nextValue,
                  };
                }}
              />
            </Stack>
            {avgDurationPreviewValues && avgDurationThresholds ? (
              <ThresholdPreviewChips
                values={avgDurationPreviewValues}
                formatter={(value) => formatDuration(value * 1000)}
                direction="lowerBetter"
                warn={avgDurationThresholds.warn}
                target={avgDurationThresholds.target}
                disabled={!enabled.avgDuration}
              />
            ) : null}
          </Stack>
        </MetricSubsection>
      </Box>

      <Box sx={{ borderTop: 1, borderColor: 'divider', mt: s('sm'), pt: s('xl') }}>
        <ThresholdSliderMetricSection
          title="Flake Score"
          enabled={enabled.flakeScore}
          warn={flakeScoreRange[1]}
          target={flakeScoreRange[0]}
          direction="lowerBetter"
          formatter={(value) => `${Math.round(value)}%`}
          previewValues={flakeScorePreviewValues}
          onToggle={(checked) => {
            setHasInteracted(true);
            setEnabled((prev) => ({ ...prev, flakeScore: checked }));
          }}
          onChange={(nextWarn, nextTarget) => {
            setHasInteracted(true);
            setDrafts((prev) => ({
              ...prev,
              flakeScore: {
                ...prev.flakeScore,
                warn: String(nextWarn),
                target: String(nextTarget),
              },
            }));
          }}
        />
      </Box>
    </Stack>
  );

  const content = (
    <Stack gap="xl">
      {sections}
      <Box sx={{ pt: s('sm') }}>{actions}</Box>
    </Stack>
  );

  if (container === 'none') {
    return content;
  }

  return (
    <ProjectSettingsAccordion
      title={title}
      description={description}
      disabled={disabled}
      disabledReason={disabledReason}
    >
      {content}
    </ProjectSettingsAccordion>
  );
};
